// v3.17 STEAMWORKS BRIDGE — offline-safe stub.
//
// This module is the ONLY place that talks to Steam. It works in three modes:
//   1) Web build: everything no-ops (console.info only in dev).
//   2) Electron build without greenworks: window.slop.steam exists but Steam SDK
//      failed to load (bad App ID, missing DLL, running outside Steam) — we
//      swallow silently so the game keeps working.
//   3) Electron build with greenworks + Steam running: real achievements fire.
//
// To ship for real: main-process code in /app/electron/main.js loads greenworks
// (if available), exposes IPC handlers, and preload.js reflects them onto
// window.slop.steam.  This file only calls that bridge if it's present.
//
// Achievement ID map: the LEFT keys are our internal achievement ids (from
// /app/frontend/src/game/achievements.js). The RIGHT values are the Steam
// Achievement IDs (all-caps convention). If a game achievement is not in this
// map, no Steam achievement fires — this lets us stage a Steam launch even if
// we haven't defined all achievements in the Steam partner backend yet.
const STEAM_ACHIEVEMENT_MAP = {
  first_blood:      'ACH_FIRST_BLOOD',
  perfect:          'ACH_PERFECT_COMBAT',
  clutch:           'ACH_CLUTCH_1HP',
  hoarder:          'ACH_GOLD_HOARDER',
  relic_hunter:     'ACH_RELIC_HUNTER',
  fat_deck:         'ACH_FAT_DECK',
  lean_deck:        'ACH_LEAN_DECK',
  cursed_deck:      'ACH_CURSED_DECK',
  cursed_win:       'ACH_CURSED_WIN',
  cursed_boss:      'ACH_CURSED_BOSS',
  daily_grinder:    'ACH_DAILY_GRINDER',
  streak_5:         'ACH_STREAK_5',
  streak_10:        'ACH_STREAK_10',
  slop_slayer:      'ACH_MIDBOSS',
  final_arc:        'ACH_FINAL_BOSS',
  rizzler_win:      'ACH_RIZZLER_WIN',
  sigma_win:        'ACH_SIGMA_WIN',
  gooner_win:       'ACH_GOONER_WIN',
  ascension_1:      'ACH_ASCENSION_1',
  ascension_3:      'ACH_ASCENSION_3',
  ascension_5:      'ACH_ASCENSION_5',
  codex_10:         'ACH_CODEX_10',
  codex_25:         'ACH_CODEX_25',
  codex_all:        'ACH_CODEX_COMPLETE',
  glass_cannon:     'ACH_GLASS_CANNON',
  power_trip:       'ACH_POWER_TRIP',
  synergy_master:   'ACH_SYNERGY',
  broke_but_woke:   'ACH_BROKE_BUT_WOKE',
  affix_slayer:     'ACH_AFFIX_SLAYER',
  set_complete:     'ACH_RELIC_SET',
  evolved:          'ACH_EVOLVED',
  status_survivor:  'ACH_STATUS_SURVIVOR',
  cards_seen_50:    'ACH_CARDS_50',
  x_cost_god:       'ACH_X_COST_GOD',
  // v3.19 CURSED CENTURY — platinum trophy
  cursed_century:   'ACH_CURSED_CENTURY',
};

function getBridge() {
  try {
    if (typeof window !== 'undefined' && window.slop && window.slop.steam) return window.slop.steam;
  } catch (e) {}
  return null;
}

let _initialized = false;
let _steamAppId = null;
let _lastError = null;

// Initialize — called once on app boot. Idempotent, never throws.
export async function initSteam() {
  if (_initialized) return { ok: !!_steamAppId, appId: _steamAppId, err: _lastError };
  _initialized = true;
  const bridge = getBridge();
  if (!bridge) return { ok: false, appId: null, err: 'no-bridge' };
  try {
    const res = await bridge.init();
    _steamAppId = res?.appId || null;
    return { ok: true, appId: _steamAppId, err: null };
  } catch (e) {
    _lastError = String(e && e.message || e);
    return { ok: false, appId: null, err: _lastError };
  }
}

export function isSteamActive() {
  return !!(_steamAppId && getBridge());
}

// Unlock a Steam achievement by internal id — safe to call anywhere.
// Web build: silent no-op. Electron: forwards to greenworks via IPC.
export async function unlockAchievement(internalId) {
  const steamId = STEAM_ACHIEVEMENT_MAP[internalId];
  if (!steamId) return { ok: false, reason: 'unmapped', internalId };
  const bridge = getBridge();
  if (!bridge) return { ok: false, reason: 'no-bridge', internalId };
  try {
    await bridge.unlockAchievement(steamId);
    return { ok: true, steamId };
  } catch (e) {
    return { ok: false, reason: 'bridge-error', err: String(e && e.message || e) };
  }
}

// Sync save-data to Steam Cloud. `saveJson` is a string; the bridge writes it
// to the Steam Cloud remote file API. In web, no-op.
export async function syncSaveCloud(saveJson) {
  const bridge = getBridge();
  if (!bridge) return { ok: false, reason: 'no-bridge' };
  try {
    await bridge.writeCloudSave(saveJson);
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: 'bridge-error', err: String(e && e.message || e) };
  }
}

export async function readCloudSave() {
  const bridge = getBridge();
  if (!bridge) return null;
  try { return await bridge.readCloudSave(); } catch (e) { return null; }
}

// Debug helper — used by tests / dev only.
export function _internal() {
  return { STEAM_ACHIEVEMENT_MAP, _initialized, _steamAppId, _lastError, bridge: getBridge() };
}
